import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Copy, Trash2, Send, ArrowUp, ArrowDown, Eye, Clock, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inp = "w-full bg-white border border-[#E8E1D8] px-3 py-2 text-sm outline-none focus:border-[#1D1D1B]";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-[#E8E1D8] bg-white p-5" data-testid={testid}>
      <p className="label-x">{label}</p>
      <p className="font-display text-2xl mt-2">{value}</p>
    </div>
  );
}

/* ============================= OVERZICHT ============================= */
export function MarketingOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/marketing/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="label-x">LADEN…</p>;
  return (
    <div data-testid="marketing-overview">
      <h1 className="heading-x text-2xl mb-6">MARKETING — OVERZICHT</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="ABONNEES" value={d.subscribers} testid="mk-subscribers" />
        <Stat label="UITGESCHREVEN" value={d.unsubscribed} />
        <Stat label="SEGMENTEN" value={d.segments} />
        <Stat label="CAMPAGNES" value={d.campaigns} />
        <Stat label="CAMPAGNES VERZONDEN" value={d.campaigns_sent} />
        <Stat label="ACTIEVE FLOWS" value={d.automations_active} />
        <Stat label="E-MAILS VERZONDEN" value={d.emails_sent} />
        <Stat label="OPEN RATIO" value={`${d.open_rate}%`} />
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/admin/marketing/campaigns" className="btn-x text-[0.65rem]">NIEUWE CAMPAGNE</Link>
        <Link to="/admin/marketing/segments" className="btn-x-outline text-[0.65rem]">SEGMENTEN</Link>
        <Link to="/admin/marketing/automations" className="btn-x-outline text-[0.65rem]">AUTOMATISERINGEN</Link>
        <Link to="/admin/marketing/analytics" className="btn-x-outline text-[0.65rem]">ANALYSE</Link>
      </div>
    </div>
  );
}

/* ============================= BLOK-EDITOR ============================= */
const BLOCK_TYPES = ["logo", "heading", "text", "image", "button", "discount", "product", "product_grid", "divider", "spacer", "footer"];
const NEW_BLOCK = {
  logo: {}, heading: { text: "Kop" }, text: { text: "Jouw tekst hier" },
  image: { url: "", href: "" }, button: { text: "SHOP NU", href: "/shop" },
  discount: { code: "", text: "Gebruik deze code bij het afrekenen" },
  product: { product_id: "" }, product_grid: { product_ids: [] },
  divider: {}, spacer: { height: 24 }, footer: { text: "ONUSA — Amsterdam" },
};

export function BlockEditor({ blocks, onChange, products }) {
  const [discounts, setDiscounts] = useState([]);
  useEffect(() => { api.get("/admin/discounts").then(({ data }) => setDiscounts(data)).catch(() => {}); }, []);
  const set = (i, k, v) => { const b = [...blocks]; b[i] = { ...b[i], [k]: v }; onChange(b); };
  const add = (type) => onChange([...blocks, { type, ...JSON.parse(JSON.stringify(NEW_BLOCK[type])) }]);
  const del = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= blocks.length) return; const b = [...blocks]; [b[i], b[j]] = [b[j], b[i]]; onChange(b); };
  return (
    <div data-testid="block-editor">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {BLOCK_TYPES.map((t) => (
          <button key={t} type="button" onClick={() => add(t)} className="label-x border border-[#E8E1D8] px-2 py-1 hover:bg-[#E8E1D8]/50" data-testid={`add-block-${t}`}>+ {t}</button>
        ))}
      </div>
      <div className="space-y-2">
        {blocks.map((b, i) => (
          <div key={i} className="border border-[#E8E1D8] bg-white p-3" data-testid={`block-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="label-x">{b.type}</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
                <button type="button" onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
                <button type="button" onClick={() => del(i)} data-testid={`del-block-${i}`}><Trash2 size={14} /></button>
              </div>
            </div>
            {b.type === "heading" && <input className={inp} value={b.text || ""} onChange={(e) => set(i, "text", e.target.value)} />}
            {b.type === "text" && <textarea rows={3} className={inp} value={b.text || ""} onChange={(e) => set(i, "text", e.target.value)} />}
            {b.type === "footer" && <input className={inp} value={b.text || ""} onChange={(e) => set(i, "text", e.target.value)} />}
            {b.type === "spacer" && <input type="number" className={inp} value={b.height || 24} onChange={(e) => set(i, "height", parseInt(e.target.value) || 24)} />}
            {b.type === "image" && (<div className="space-y-2"><input placeholder="Afbeelding URL" className={inp} value={b.url || ""} onChange={(e) => set(i, "url", e.target.value)} /><input placeholder="Link (optioneel)" className={inp} value={b.href || ""} onChange={(e) => set(i, "href", e.target.value)} /></div>)}
            {b.type === "button" && (<div className="grid grid-cols-2 gap-2"><input placeholder="Label" className={inp} value={b.text || ""} onChange={(e) => set(i, "text", e.target.value)} /><input placeholder="Link" className={inp} value={b.href || ""} onChange={(e) => set(i, "href", e.target.value)} /></div>)}
            {b.type === "discount" && (<div className="space-y-2">
              <input list="mk-discount-codes" placeholder="KORTINGSCODE" className={inp} value={b.code || ""} onChange={(e) => set(i, "code", e.target.value.toUpperCase())} data-testid={`block-discount-code-${i}`} />
              <datalist id="mk-discount-codes">{discounts.map((d) => <option key={d.id} value={d.code}>{d.type === "percentage" ? `${d.value}%` : `€${d.value}`}</option>)}</datalist>
              <input placeholder="Label boven de code" className={inp} value={b.text || ""} onChange={(e) => set(i, "text", e.target.value)} />
            </div>)}
            {b.type === "product" && (
              <select className={inp} value={b.product_id || ""} onChange={(e) => set(i, "product_id", e.target.value)}>
                <option value="">— kies product —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>)}
            {b.type === "product_grid" && (
              <div className="flex flex-wrap gap-1.5">
                {products.map((p) => {
                  const on = (b.product_ids || []).includes(p.id);
                  return <button key={p.id} type="button" onClick={() => { const ids = on ? b.product_ids.filter((x) => x !== p.id) : [...(b.product_ids || []), p.id]; set(i, "product_ids", ids); }} className={`label-x border px-2 py-1 ${on ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8]"}`}>{p.name}</button>;
                })}
              </div>)}
          </div>
        ))}
        {blocks.length === 0 && <p className="label-x py-6 text-center">VOEG BLOKKEN TOE OM JE E-MAIL OP TE BOUWEN</p>}
      </div>
    </div>
  );
}

function PreviewButton({ blocks, previewText }) {
  const [html, setHtml] = useState(null);
  const open = async () => { try { const { data } = await api.post("/admin/marketing/preview-html", { blocks, preview_text: previewText || "" }); setHtml(data.html); } catch (e) { toast.error("Voorbeeld mislukt"); } };
  return (<>
    <button type="button" onClick={open} className="btn-x-outline text-[0.65rem]" data-testid="preview-email-btn"><Eye size={13} /> VOORBEELD</button>
    <Dialog open={!!html} onOpenChange={(o) => !o && setHtml(null)}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto no-scrollbar">
        <DialogHeader><DialogTitle className="heading-x text-sm">E-MAILVOORBEELD</DialogTitle></DialogHeader>
        <iframe title="voorbeeld" srcDoc={html} className="w-full h-[70vh] border border-[#E8E1D8]" />
      </DialogContent>
    </Dialog>
  </>);
}

/* ============================= CAMPAGNES ============================= */
export function MarketingCampaigns() {
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/marketing/campaigns").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/marketing/segments").then(({ data }) => setSegments(data)).catch(() => {});
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const create = async () => {
    const { data } = await api.post("/admin/marketing/campaigns", { name: "Nieuwe campagne", subject: "", blocks: [] });
    setEditing(data); load();
  };
  const del = async (id) => { if (!window.confirm("Campagne verwijderen?")) return; await api.delete(`/admin/marketing/campaigns/${id}`); load(); };
  const dup = async (id) => { await api.post(`/admin/marketing/campaigns/${id}/duplicate`); toast.success("Gedupliceerd"); load(); };

  return (
    <div data-testid="marketing-campaigns">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-x text-2xl">CAMPAGNES</h1>
        <button onClick={create} className="btn-x text-[0.65rem]" data-testid="new-campaign-btn"><Plus size={14} /> NIEUWE CAMPAGNE</button>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-3" data-testid={`campaign-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.name}</p>
              <p className="label-x mt-0.5">{c.status.toUpperCase()} · {c.subject || "geen onderwerp"} {c.status === "sent" && `· ${c.stats?.sent || 0} verzonden`}</p>
            </div>
            <button onClick={() => setEditing(c)} className="label-x" data-testid={`edit-campaign-${c.id}`}>BEWERK</button>
            <button onClick={() => dup(c.id)} title="Dupliceer"><Copy size={15} /></button>
            <button onClick={() => del(c.id)} title="Verwijder"><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">NOG GEEN CAMPAGNES</p>}
      </div>
      {editing && <CampaignEditor campaign={editing} segments={segments} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CampaignEditor({ campaign, segments, products, onClose, onSaved }) {
  const [c, setC] = useState({ ...campaign, blocks: campaign.blocks || [] });
  const [testEmail, setTestEmail] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const isSent = c.status === "sent";

  const save = async () => {
    try { await api.put(`/admin/marketing/campaigns/${c.id}`, { name: c.name, subject: c.subject, preview_text: c.preview_text, segment_id: c.segment_id, blocks: c.blocks }); toast.success("Opgeslagen"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendTest = async () => {
    if (!testEmail) return toast.error("Voer een test-e-mail in");
    await api.put(`/admin/marketing/campaigns/${c.id}`, { name: c.name, subject: c.subject, preview_text: c.preview_text, segment_id: c.segment_id, blocks: c.blocks });
    try { const { data } = await api.post(`/admin/marketing/campaigns/${c.id}/test`, { email: testEmail }); toast.success(`Test ${data.status}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendNow = async () => {
    if (!c.segment_id) return toast.error("Kies eerst een segment");
    if (!window.confirm("Deze campagne nu verzenden naar alle ingeschreven leden van het segment?")) return;
    await api.put(`/admin/marketing/campaigns/${c.id}`, { name: c.name, subject: c.subject, preview_text: c.preview_text, segment_id: c.segment_id, blocks: c.blocks });
    try { const { data } = await api.post(`/admin/marketing/campaigns/${c.id}/send`); toast.success(`Verzonden naar ${data.sent}/${data.recipients}`); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doSchedule = async () => {
    if (!c.segment_id) return toast.error("Kies eerst een segment");
    if (!schedTime) return toast.error("Kies datum en tijd");
    await api.put(`/admin/marketing/campaigns/${c.id}`, { name: c.name, subject: c.subject, preview_text: c.preview_text, segment_id: c.segment_id, blocks: c.blocks });
    try { await api.post(`/admin/marketing/campaigns/${c.id}/schedule`, { scheduled_at: new Date(schedTime).toISOString() }); toast.success("Campagne ingepland"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancelSchedule = async () => {
    try { await api.post(`/admin/marketing/campaigns/${c.id}/cancel`); toast.success("Planning geannuleerd"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
        <DialogHeader><DialogTitle className="heading-x text-sm">{isSent ? "CAMPAGNE (VERZONDEN)" : "CAMPAGNE BEWERKEN"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="label-x">NAAM</span><input className={inp} value={c.name} onChange={(e) => set("name", e.target.value)} data-testid="camp-name" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label><span className="label-x">ONDERWERP</span><input className={inp} value={c.subject || ""} onChange={(e) => set("subject", e.target.value)} data-testid="camp-subject" /></label>
            <label><span className="label-x">PREVIEW-TEKST</span><input className={inp} value={c.preview_text || ""} onChange={(e) => set("preview_text", e.target.value)} /></label>
          </div>
          <label className="block"><span className="label-x">DOELGROEP (SEGMENT)</span>
            <select className={inp} value={c.segment_id || ""} onChange={(e) => set("segment_id", e.target.value)} data-testid="camp-segment">
              <option value="">— kies segment —</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.member_count ?? "?"} leden)</option>)}
            </select>
          </label>
          <div><span className="label-x">E-MAILINHOUD</span><div className="mt-2"><BlockEditor blocks={c.blocks} onChange={(b) => set("blocks", b)} products={products} /></div></div>
          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-[#E8E1D8]">
            <label className="flex-1 min-w-[180px]"><span className="label-x">TEST-E-MAIL</span><input className={inp} value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="camp-test-email" /></label>
            <button onClick={sendTest} className="btn-x-outline text-[0.65rem]" data-testid="camp-send-test"><Send size={13} /> TEST</button>
            <PreviewButton blocks={c.blocks} previewText={c.preview_text} />
          </div>
          {!isSent && (
            <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-[#E8E1D8]">
              <label className="flex-1 min-w-[200px]"><span className="label-x">INPLANNEN (DATUM & TIJD)</span><input type="datetime-local" className={inp} value={schedTime} onChange={(e) => setSchedTime(e.target.value)} data-testid="camp-sched-time" /></label>
              <button onClick={doSchedule} className="btn-x-outline text-[0.65rem]" data-testid="camp-schedule"><Clock size={13} /> INPLANNEN</button>
              {c.status === "scheduled" && <button onClick={cancelSchedule} className="btn-x-outline text-[0.65rem]" data-testid="camp-cancel-schedule">ANNULEER PLANNING</button>}
            </div>
          )}
          {c.status === "scheduled" && c.scheduled_at && <p className="label-x text-[#77736D]" data-testid="camp-scheduled-at">INGEPLAND VOOR: {new Date(c.scheduled_at).toLocaleString("nl-NL")}</p>}
          <div className="flex gap-3 pt-2">
            {!isSent && <button onClick={save} className="btn-x" data-testid="camp-save">CONCEPT OPSLAAN</button>}
            {!isSent && <button onClick={sendNow} className="btn-x" data-testid="camp-send-now"><Send size={14} /> NU VERZENDEN</button>}
            <button onClick={onClose} className="btn-x-outline">SLUITEN</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================= SEGMENTEN ============================= */
const FIELDS = [
  ["order_count", "Aantal bestellingen"], ["total_spend", "Totale besteding (€)"], ["avg_order_value", "Gem. orderwaarde (€)"],
  ["last_order_days_ago", "Dagen sinds laatste bestelling"], ["first_order_days_ago", "Dagen sinds eerste bestelling"],
  ["product_purchased", "Product gekocht (naam/id)"], ["category_purchased", "Kleur/categorie gekocht"],
  ["consent", "Marketing-toestemming"], ["country", "Land"], ["tag", "Klant-tag"],
];
const OPS = { number: [["gte", "≥"], ["lte", "≤"], ["eq", "="]], text: [["eq", "is"]] };
const NUM = new Set(["order_count", "total_spend", "avg_order_value", "last_order_days_ago", "first_order_days_ago"]);

export function MarketingSegments() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/marketing/segments").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Segment verwijderen?")) return; await api.delete(`/admin/marketing/segments/${id}`); load(); };
  return (
    <div data-testid="marketing-segments">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-x text-2xl">SEGMENTEN</h1>
        <button onClick={() => setEditing({ name: "", description: "", match: "all", rules: [] })} className="btn-x text-[0.65rem]" data-testid="new-segment-btn"><Plus size={14} /> NIEUW SEGMENT</button>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-4 p-3" data-testid={`segment-${s.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium">{s.name}</p><p className="label-x mt-0.5">{s.rules?.length || 0} regels · {s.member_count ?? "?"} leden</p></div>
            <button onClick={() => setEditing(s)} className="label-x" data-testid={`edit-segment-${s.id}`}>BEWERK</button>
            <button onClick={() => del(s.id)}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">NOG GEEN SEGMENTEN</p>}
      </div>
      {editing && <SegmentEditor seg={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SegmentEditor({ seg, onClose, onSaved }) {
  const [s, setS] = useState({ ...seg, rules: seg.rules || [] });
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setRule = (i, k, v) => { const r = [...s.rules]; r[i] = { ...r[i], [k]: v }; setS({ ...s, rules: r }); };
  const addRule = () => setS({ ...s, rules: [...s.rules, { field: "order_count", op: "gte", value: "1" }] });
  const delRule = (i) => setS({ ...s, rules: s.rules.filter((_, idx) => idx !== i) });

  const doPreview = async () => { try { const { data } = await api.post("/admin/marketing/segments/preview", { name: s.name, match: s.match, rules: s.rules }); setPreview(data); } catch (e) { toast.error("Voorbeeld mislukt"); } };
  const save = async () => {
    if (!s.name) return toast.error("Naam is verplicht");
    try { if (s.id) await api.put(`/admin/marketing/segments/${s.id}`, { name: s.name, description: s.description, match: s.match, rules: s.rules }); else await api.post("/admin/marketing/segments", { name: s.name, description: s.description, match: s.match, rules: s.rules }); toast.success("Opgeslagen"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
        <DialogHeader><DialogTitle className="heading-x text-sm">{s.id ? "SEGMENT BEWERKEN" : "NIEUW SEGMENT"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="label-x">NAAM</span><input className={inp} value={s.name} onChange={(e) => set("name", e.target.value)} data-testid="seg-name" /></label>
          <label className="block"><span className="label-x">MATCH</span>
            <select className={inp} value={s.match} onChange={(e) => set("match", e.target.value)} data-testid="seg-match">
              <option value="all">ALLE regels (EN)</option><option value="any">ELKE regel (OF)</option>
            </select>
          </label>
          <div>
            <div className="flex items-center justify-between"><span className="label-x">REGELS</span><button onClick={addRule} className="label-x" data-testid="seg-add-rule">+ REGEL TOEVOEGEN</button></div>
            <div className="space-y-2 mt-2">
              {s.rules.map((r, i) => {
                const isNum = NUM.has(r.field);
                return (
                  <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center" data-testid={`seg-rule-${i}`}>
                    <select className={inp} value={r.field} onChange={(e) => setRule(i, "field", e.target.value)}>{FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                    <select className={inp} value={r.op} onChange={(e) => setRule(i, "op", e.target.value)}>{(isNum ? OPS.number : OPS.text).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                    {r.field === "consent" ? (
                      <select className={inp} value={r.value} onChange={(e) => setRule(i, "value", e.target.value)}><option value="subscribed">ingeschreven</option><option value="unsubscribed">uitgeschreven</option></select>
                    ) : <input className={inp} value={r.value} onChange={(e) => setRule(i, "value", e.target.value)} />}
                    <button onClick={() => delRule(i)}><Trash2 size={14} /></button>
                  </div>
                );
              })}
              {s.rules.length === 0 && <p className="label-x">Geen regels = alle klanten & abonnees.</p>}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-[#E8E1D8]">
            <button onClick={doPreview} className="btn-x-outline text-[0.65rem]" data-testid="seg-preview">AANTAL BEKIJKEN</button>
            {preview && <span className="label-x" data-testid="seg-preview-result">{preview.count} leden · {preview.subscribed} ingeschreven</span>}
          </div>
          {preview?.sample?.length > 0 && (
            <div className="max-h-40 overflow-y-auto no-scrollbar border border-[#E8E1D8] bg-white p-2 text-xs">
              {preview.sample.map((m, i) => <div key={i} className="flex justify-between py-0.5"><span>{m.email}</span><span className="text-[#77736D]">{m.order_count}b · €{m.total_spend} · {m.consent}</span></div>)}
            </div>)}
          <div className="flex gap-3 pt-2"><button onClick={save} className="btn-x" data-testid="seg-save">SEGMENT OPSLAAN</button><button onClick={onClose} className="btn-x-outline">ANNULEER</button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================= SJABLONEN ============================= */
export function MarketingTemplates() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/marketing/templates").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);
  const create = async () => { const { data } = await api.post("/admin/marketing/templates", { name: "Nieuw sjabloon", blocks: [] }); setEditing(data); load(); };
  const del = async (id) => { if (!window.confirm("Sjabloon verwijderen?")) return; await api.delete(`/admin/marketing/templates/${id}`); load(); };
  return (
    <div data-testid="marketing-templates">
      <div className="flex items-center justify-between mb-6"><h1 className="heading-x text-2xl">E-MAILSJABLONEN</h1><button onClick={create} className="btn-x text-[0.65rem]" data-testid="new-template-btn"><Plus size={14} /> NIEUW SJABLOON</button></div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((t) => (
          <div key={t.id} className="flex items-center gap-4 p-3" data-testid={`template-${t.id}`}>
            <span className="flex-1 text-sm font-medium">{t.name} <span className="label-x">{t.blocks?.length || 0} blokken</span></span>
            <button onClick={() => setEditing(t)} className="label-x">BEWERK</button>
            <button onClick={() => del(t.id)}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">NOG GEEN SJABLONEN</p>}
      </div>
      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
            <DialogHeader><DialogTitle className="heading-x text-sm">SJABLOON BEWERKEN</DialogTitle></DialogHeader>
            <label className="block mb-3"><span className="label-x">NAAM</span><input className={inp} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tpl-name" /></label>
            <BlockEditor blocks={editing.blocks || []} onChange={(b) => setEditing({ ...editing, blocks: b })} products={products} />
            <div className="flex gap-3 mt-4"><PreviewButton blocks={editing.blocks || []} />
              <button onClick={async () => { await api.put(`/admin/marketing/templates/${editing.id}`, { name: editing.name, blocks: editing.blocks }); toast.success("Opgeslagen"); setEditing(null); load(); }} className="btn-x" data-testid="tpl-save">SJABLOON OPSLAAN</button>
              <button onClick={() => setEditing(null)} className="btn-x-outline">ANNULEER</button></div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ============================= AUTOMATIONS ============================= */
export function MarketingAutomations() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/marketing/automations").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);
  const toggle = async (a) => { await api.put(`/admin/marketing/automations/${a.id}`, { enabled: !a.enabled }); load(); };
  return (
    <div data-testid="marketing-automations">
      <h1 className="heading-x text-2xl mb-6">AUTOMATISERINGEN / FLOWS</h1>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-4 p-4" data-testid={`automation-${a.key}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{a.name}</p>
              <p className="label-x mt-0.5">TRIGGER: {a.trigger} {a.delay_minutes ? `· wacht ${a.delay_minutes}m` : ""} {a.key === "win_back" ? `· ${a.inactivity_days || 60}d inactief` : ""} · {a.stats?.sent || 0} verzonden</p>
            </div>
            <button onClick={() => setEditing({ ...a, blocks: a.blocks || [] })} className="label-x" data-testid={`edit-automation-${a.key}`}>BEWERK</button>
            <button onClick={() => toggle(a)} className={`label-x px-3 py-1.5 border ${a.enabled ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8]"}`} data-testid={`toggle-automation-${a.key}`}>{a.enabled ? "AAN" : "UIT"}</button>
          </div>
        ))}
      </div>
      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
            <DialogHeader><DialogTitle className="heading-x text-sm">FLOW BEWERKEN — {editing.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <label className="block"><span className="label-x">ONDERWERP</span><input className={inp} value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="auto-subject" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label><span className="label-x">VERTRAGING (MINUTEN)</span><input type="number" className={inp} value={editing.delay_minutes ?? 0} onChange={(e) => setEditing({ ...editing, delay_minutes: parseInt(e.target.value) || 0 })} /></label>
                {editing.key === "win_back" && <label><span className="label-x">DAGEN INACTIEF</span><input type="number" className={inp} value={editing.inactivity_days ?? 60} onChange={(e) => setEditing({ ...editing, inactivity_days: parseInt(e.target.value) || 60 })} /></label>}
              </div>
              <BlockEditor blocks={editing.blocks || []} onChange={(b) => setEditing({ ...editing, blocks: b })} products={products} />
              <div className="flex gap-3"><PreviewButton blocks={editing.blocks || []} />
                <button onClick={async () => { await api.put(`/admin/marketing/automations/${editing.id}`, { subject: editing.subject, delay_minutes: editing.delay_minutes, inactivity_days: editing.inactivity_days, blocks: editing.blocks }); toast.success("Opgeslagen"); setEditing(null); load(); }} className="btn-x" data-testid="auto-save">FLOW OPSLAAN</button>
                <button onClick={() => setEditing(null)} className="btn-x-outline">ANNULEER</button></div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ============================= ANALYSE ============================= */
export function MarketingAnalytics() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/marketing/analytics").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div data-testid="marketing-analytics">
      <h1 className="heading-x text-2xl mb-6">CAMPAGNE-ANALYSE</h1>
      <div className="overflow-x-auto border border-[#E8E1D8] bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E8E1D8] text-left">
            {["CAMPAGNE", "VERZONDEN", "GEOPEND", "GEKLIKT", "UITGESCHR.", "OPEN %", "KLIK %", "BESTELLINGEN", "OMZET"].map((h) => <th key={h} className="label-x p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E8E1D8]" data-testid={`analytics-${r.id}`}>
                <td className="p-3">{r.name}<br /><span className="label-x">{r.subject}</span></td>
                <td className="p-3 font-mono">{r.sent}</td><td className="p-3 font-mono">{r.opened}</td>
                <td className="p-3 font-mono">{r.clicked}</td><td className="p-3 font-mono">{r.unsubscribed}</td>
                <td className="p-3 font-mono">{r.open_rate}%</td><td className="p-3 font-mono">{r.click_rate}%</td>
                <td className="p-3 font-mono">{r.orders_attributed}</td><td className="p-3 font-mono">€{r.revenue_attributed}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="label-x p-8 text-center">NOG GEEN VERZONDEN CAMPAGNES</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="label-x mt-3 text-[#77736D]">Toegewezen bestellingen/omzet = betaalde bestellingen door ontvangers binnen 7 dagen na verzending. Open-/klik-tracking via pixel & ingepakte links.</p>
    </div>
  );
}

/* ============================= CONTACTEN ============================= */
export function MarketingContacts() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = (query = q) => api.get(`/admin/marketing/contacts?q=${encodeURIComponent(query)}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(""); }, []);
  const openDetail = async (email) => { try { const { data } = await api.get(`/admin/marketing/customer/${encodeURIComponent(email)}`); setDetail(data); } catch { toast.error("Kon contact niet laden"); } };
  return (
    <div data-testid="marketing-contacts">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="heading-x text-2xl">CONTACTEN</h1>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <input placeholder="Zoek e-mail of naam" value={q} onChange={(e) => setQ(e.target.value)} className={inp + " w-56"} data-testid="contacts-search" />
          <button className="btn-x-outline text-[0.65rem]" data-testid="contacts-search-btn"><Search size={13} /> ZOEK</button>
        </form>
      </div>
      <div className="overflow-x-auto border border-[#E8E1D8] bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E8E1D8] text-left">{["E-MAIL", "NAAM", "BESTELLINGEN", "BESTEDING", "TOESTEMMING", "LAATSTE BESTELLING"].map((h) => <th key={h} className="label-x p-3">{h}</th>)}</tr></thead>
          <tbody>
            {(data?.contacts || []).map((r) => (
              <tr key={r.email} className="border-b border-[#E8E1D8] cursor-pointer hover:bg-[#F7F5F1]" onClick={() => openDetail(r.email)} data-testid={`contact-row-${r.email}`}>
                <td className="p-3">{r.email}</td><td className="p-3">{r.name || "—"}</td>
                <td className="p-3 font-mono">{r.order_count}</td><td className="p-3 font-mono">€{r.total_spend}</td>
                <td className="p-3"><span className="label-x">{r.consent}</span></td>
                <td className="p-3 font-mono">{r.last_order_days_ago == null ? "—" : `${r.last_order_days_ago}d`}</td>
              </tr>
            ))}
            {data && data.contacts.length === 0 && <tr><td colSpan={6} className="label-x p-8 text-center">GEEN CONTACTEN</td></tr>}
          </tbody>
        </table>
      </div>
      {data && <p className="label-x mt-3 text-[#77736D]">{data.total} contacten totaal</p>}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl bg-[#F7F5F1] max-h-[90vh] overflow-y-auto no-scrollbar" data-testid="contact-detail">
          <DialogHeader><DialogTitle className="heading-x text-sm">{detail?.email}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-[#E8E1D8] bg-white p-3"><p className="label-x">TOESTEMMING</p><p className="mt-1">{detail.consent?.status}</p></div>
                <div className="border border-[#E8E1D8] bg-white p-3"><p className="label-x">BESTELLINGEN</p><p className="mt-1 font-mono">{detail.stats?.order_count ?? 0}</p></div>
                <div className="border border-[#E8E1D8] bg-white p-3"><p className="label-x">BESTEDING</p><p className="mt-1 font-mono">€{detail.stats?.total_spend ?? 0}</p></div>
                <div className="border border-[#E8E1D8] bg-white p-3"><p className="label-x">GEM. ORDER</p><p className="mt-1 font-mono">€{detail.stats?.aov ?? 0}</p></div>
              </div>
              <div><p className="label-x mb-1">SEGMENTEN</p><p>{detail.segments?.length ? detail.segments.join(", ") : "—"}</p></div>
              <div><p className="label-x mb-1">RECENTE BESTELLINGEN</p>{(detail.orders || []).slice(0, 5).map((o) => <div key={o.number} className="flex justify-between border-b border-[#E8E1D8] py-1"><span>{o.number}</span><span className="text-[#77736D]">{o.payment_status} · €{o.total}</span></div>)}{(!detail.orders || detail.orders.length === 0) && <p className="text-[#77736D]">Geen bestellingen</p>}</div>
              <div><p className="label-x mb-1">RECENTE E-MAILS</p>{(detail.deliveries || []).slice(0, 8).map((d) => <div key={d.id} className="flex justify-between border-b border-[#E8E1D8] py-1"><span className="truncate">{d.status}{d.opened_at ? " · geopend" : ""}</span><span className="text-[#77736D]">{(d.sent_at || "").slice(0, 10)}</span></div>)}{(!detail.deliveries || detail.deliveries.length === 0) && <p className="text-[#77736D]">Nog geen e-mails</p>}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
